import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
// store
import { responseTypesSelector } from 'store/reducers/riskTemplates'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
import { useInitialValues } from 'hooks/useInitialValues'
import { useInitialValuesLoading } from 'hooks/risks/useInitialValuesLoading'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// types
import { IFieldValueMapItem, IRiskCustomColumn } from 'types/graphql'
// utils
import { maxBy, groupBy, map } from 'lodash'
import { getFieldsInitialValues, getFormInitialValues } from 'utils/form'

export const useFormInitialValues = (
  isLoading: boolean,
  fieldValues: IFieldValueMapItem[],
  customColumns?: IRiskCustomColumn[]
) => {
  const router = useRouter()

  const isPublic = React.useContext(PublicContext)

  const { client } = useApolloClient()

  const responseTypeSets = useSelector(responseTypesSelector)

  const { riskTemplate, basicFields } = React.useContext(RiskTemplateAPIContext)

  const parentFieldValues: IFieldValueMapItem[] =
    customColumns
      ?.filter(({ fieldValue }) => fieldValue)
      .map(({ fieldValue }) => ({
        fieldName: fieldValue.field.name,
        fieldId: fieldValue.field.id,
        fieldType: fieldValue.field.fieldType,
        fieldValue: fieldValue.value,
        fieldValueId: fieldValue.id,
        field: fieldValue.field,
        createdAt: fieldValue.createdAt
      })) || []

  const parentFields =
    customColumns
      ?.filter(({ fieldValue }) => fieldValue)
      .map(({ fieldValue: { field } }) => ({ ...field, active: true })) || []

  // filter fieldValues duplicates
  const latestValues = map(groupBy([...fieldValues, ...parentFieldValues], 'fieldId'), (items) => {
    const hasLinked = items.some((item) => item.fieldLinkId)

    return hasLinked ? items : maxBy(items, 'createdAt')
  })
    .flat()
    .filter(Boolean)

  const normalizedFieldValues = latestValues.map((item) => {
    if (item.linkedFieldValue) {
      return {
        ...item,
        fieldValue: item.linkedFieldValue?.fieldValue,
        linkedRiskId: item.fieldValue.value,
        linkedFieldValueId: item.linkedFieldValue?.fieldValueId
      }
    }
    return item
  })

  const getInitialValues = async () => {
    if (isLoading) {
      return {}
    }

    const fieldsInitialValues = await getFieldsInitialValues({
      client,
      isPublic,
      isRisk: true,
      domain: router.query.domain as string,
      fields: [...basicFields, ...parentFields],
      fieldValues: normalizedFieldValues,
      responseTypeSets
    })

    const fieldsInitialValuesMap = Object.fromEntries(
      Object.entries(fieldsInitialValues).map(([key, value]) => [key, value])
    )
    const formsInitialValues = await getFormInitialValues({
      client,
      isPublic,
      riskTemplate,
      domain: router.query.domain as string,
      fieldValues: normalizedFieldValues
    })

    return { ...fieldsInitialValuesMap, ...formsInitialValues }
  }

  const initialValues = useInitialValues(getInitialValues, isLoading, JSON.stringify(fieldValues))

  const isInitialValuesLoaded = useInitialValuesLoading(!isLoading, normalizedFieldValues, initialValues)

  return { initialValues, isInitialValuesLoaded }
}
